import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { InfoIcon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { buildTaxBasisTooltip, type TaxBasisFeeRow, type TaxBasisNote } from './taxBasisTooltip';

/* The fee grids read the note through React context rather than through their memoized column
   definitions: AG Grid renders React cells in portals under the grid, so context reaches them, and
   the note arriving later re-renders just the cells instead of rebuilding the columns. */
const TaxBasisNoteContext = createContext<TaxBasisNote | null>(null);

export function TaxBasisNoteProvider({ note, children }: { note: TaxBasisNote | null; children: ReactNode }) {
  return (
    <TaxBasisNoteContext.Provider value={note}>
      <TooltipProvider>{children}</TooltipProvider>
    </TaxBasisNoteContext.Provider>
  );
}

/* Loads the note whenever `reloadKey` changes (compared by identity, so a freshly loaded record
   works as well as a string); a null or undefined key means nothing to load. Any failure leaves the
   note null (no bubble) — the grid never waits on, or breaks over, this call. */
export function useTaxBasisNote(load: () => Promise<TaxBasisNote | null | undefined>, reloadKey: unknown): TaxBasisNote | null {
  const [note, setNote] = useState<TaxBasisNote | null>(null);

  useEffect(() => {
    if (reloadKey == null || reloadKey === '') {
      setNote(null);
      return undefined;
    }
    let cancelled = false;
    load()
      .then((result) => {
        if (!cancelled) setNote(result && typeof result === 'object' ? result : null);
      })
      .catch(() => {
        if (!cancelled) setNote(null);
      });
    return () => {
      cancelled = true;
    };
    // `load` is a fresh closure every render; the key is what says a reload is due.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  return note;
}

/* Row data a fee grid attaches to each of its fee rows so the cell can find the fee behind it. */
export interface TaxBasisRowData {
  taxBasisRow?: TaxBasisFeeRow;
}

interface TaxBasisFeeNameCellProps {
  value?: unknown;
  valueFormatted?: string | null;
  data?: TaxBasisRowData;
}

/* AG Grid cell renderer (or the group column's innerRenderer) for a fee name: the name, plus an ⓘ
   explaining the state's tax basis when this row is a tax and a special rule is in force. */
export function TaxBasisFeeNameCell({ value, valueFormatted, data }: TaxBasisFeeNameCellProps) {
  const note = useContext(TaxBasisNoteContext);
  const tooltip = useMemo(() => buildTaxBasisTooltip(note, data?.taxBasisRow), [note, data?.taxBasisRow]);
  const label = valueFormatted ?? (value == null ? '' : String(value));

  if (!tooltip) return <>{label}</>;

  return (
    <span className="inline-flex items-center gap-1">
      <span>{label}</span>
      <Tooltip>
        <TooltipTrigger
          aria-label="How this tax is calculated"
          className="inline-flex cursor-help items-center border-0 bg-transparent p-0 text-muted-foreground hover:text-primary"
        >
          <InfoIcon className="size-3.5" />
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-sm flex-col items-start gap-1 text-left">
          {tooltip.headlines.map((headline) => (
            <span key={headline} className="font-semibold">
              {headline}
            </span>
          ))}
          {tooltip.calculation && <span>{tooltip.calculation}</span>}
          {tooltip.citations.map((citation) => (
            <span key={citation} className="text-background/70">
              {citation}
            </span>
          ))}
        </TooltipContent>
      </Tooltip>
    </span>
  );
}
